import {
  Label,
  Input,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import type { ChangeEvent } from "react"
import {
  useCallback,
  useState,
} from "react"
import styles from "./AccountDetails.module.scss"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import {
  useFollow,
  useUpdateFollow,
} from "src/hooks"
import { EditNicknameErrorToast } from "src/toasts/EditNicknameErrorToast"

const polyglotPrefix = "pages.account.account_details.nickname."
const nicknameId = "display-name-input"

interface Props {
  accountId: number;
}

export function Nickname(props: Props) {
  const { accountId } = props

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const follow = useFollow(accountId)

  const updateFollow = useUpdateFollow()

  const currentNickname = follow?.nickname

  const [
    nickname,
    setNickname,
  ] = useState<string>(currentNickname || "")

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setNickname(e.target.value)
    },
    [setNickname],
  )

  const handleNicknameUpdate = useCallback(
    async () => {
      if (nickname === currentNickname) {
        return
      }

      setIsOverlayLoaderVisible(true)

      try {
        if (!updateFollow) {
          throw new Error("updateFollow is not defined")
        }

        if (!follow?.id) {
          throw new Error("follow is not defined")
        }

        await updateFollow({
          id: follow.id,
          nickname: nickname || null,
        })
      } catch {
        showToast(
          "edit-nickname-error",
          EditNicknameErrorToast,
        )
      }

      setIsOverlayLoaderVisible(false)
    },
    [
      nickname,
      currentNickname,
      follow?.id,
      showToast,
      updateFollow,
      setIsOverlayLoaderVisible,
    ],
  )

  const onBlur = useCallback(
    () => {
      void handleNicknameUpdate()
    },
    [handleNicknameUpdate],
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        void handleNicknameUpdate()
      }
    },
    [handleNicknameUpdate],
  )

  return (
    <div>
      <Label
        className={styles.inputLabel}
        htmlFor={nicknameId}
      >
        {polyglot.t(`${polyglotPrefix}label`)}
      </Label>
      <Input
        className={styles.nicknameInput}
        id={nicknameId}
        value={nickname ?? ""}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        autoComplete="nickname"
        maxLength={60}
      />
    </div>
  )
}
